import { useEffect, useState } from "react";
import SupportDrawer from "../drawers/SupportDrawer";
import { BuildingMultipleFilled, BuildingMultipleRegular, bundleIcon, ClipboardEditFilled, ClipboardEditRegular, MailMultipleFilled, MailMultipleRegular, MoreVertical24Regular, PeopleTeamFilled, PeopleTeamRegular, PersonSettingsFilled, PersonSettingsRegular, QuestionCircleFilled, SettingsFilled, SettingsRegular, SignOutFilled, SignOutRegular, TextBulletListSquareSearchFilled, TextBulletListSquareSearchRegular, WrenchFilled, WrenchRegular } from "@fluentui/react-icons";
import { Menu, MenuButton, MenuItem, MenuList, MenuPopover, MenuTrigger } from "@fluentui/react-components";
import UsersDrawer from "../drawers/UsersDrawer";
import UserSettingsDialog from "../dialogs/UserSettingsDialog";
import { useAppContext } from "../../AppContext";
import authService from "../../services/authService";
import NewsDrawer from "../drawers/NewsDrawer";
import System from "../dialogs/System/System";
import UserGroupsDialog from "../dialogs/UserGroupsDialog";
import { TaskData, TaskListItem } from "../../models/task";
import { getTasks } from "../../services/taskService";
import { getUserGroupsByModuleAndOrganizationID } from "../../services/userGroupService";
import { RoleDTO } from "../../models/role";

const Header = () => {
  const [showSupportDrawer, setShowSupportDrawer] = useState(false);
  const [showUserDrawer, setShowUserDrawer] = useState(false);
  const [showSettingsDialog, setShowSettingDialog] = useState(false);
  const [openNewsDrawer, setOpenNewsDrawer] = useState<boolean>(false);
  const [showSystemDialog, setShowSystemDialog] = useState(false);

  const { user, setUser, featurePermissions, setUserTasks } = useAppContext();

  const PersonSettingIcon = bundleIcon(PersonSettingsFilled, PersonSettingsRegular);
  const WrenchIcon = bundleIcon(WrenchFilled, WrenchRegular)
  const PeopleTeamIcon = bundleIcon(PeopleTeamFilled, PeopleTeamRegular);
  const BuildingMultipleIcon = bundleIcon(BuildingMultipleFilled, BuildingMultipleRegular);
  const SettingsIcon = bundleIcon(SettingsFilled, SettingsRegular);
  const SignOutIcon = bundleIcon(SignOutFilled, SignOutRegular);
  const ClipboardEditIcon = bundleIcon(ClipboardEditFilled, ClipboardEditRegular);
  const MailMultipleIcon = bundleIcon(MailMultipleFilled,  MailMultipleRegular);
  const TextBulletSearchIcon = bundleIcon(TextBulletListSquareSearchFilled, TextBulletListSquareSearchRegular);

  useEffect(() => {
    const fetchUserTasks = async () => {
      try{
        // userTasks represents requests still awaiting an answer from an admin, so only
        // fetch non-completed ones here (completed/answered ones belong in Ansökningar instead).
        const result = await getTasks(false, 1000, 0, 5);
        if (user.organization.organizationTypeID !== 1) {
          const administeredGroupIDs = await getKontoadminGroupIDs();
          setUserTasks(filterTasks(result.tasks, administeredGroupIDs));
        } else {
          setUserTasks(result.tasks);
        }
      }catch(error){
        console.error("Error fetching tasks:", error);
      }
    }

    // A Kontoadmin handles the requests for every group of their own organization in the ramavtal they
    // administer, whether or not they are a member of those groups.
    const getKontoadminGroupIDs = async (): Promise<Set<number>> => {
      const moduleIDs: number[] = Array.from(new Set(
        (user.assignments ?? [])
          .filter((a: any) => a.role.roleName === 'Kontoadmin')
          .map((a: any) => a.module.moduleID as number)));

      if (moduleIDs.length === 0) return new Set<number>();

      const groupsPerModule = await Promise.all(
        moduleIDs.map(id => getUserGroupsByModuleAndOrganizationID(id, user.organization.organizationID)));

      return new Set<number>(groupsPerModule.flat().map((g: any) => g.userGroupID));
    }

    const filterTasks = (tasks: TaskListItem[], kontoadminGroupIDs: Set<number>) => {
      const filteredTasks = tasks.filter(t => {
        const taskData = getTaskData(t.taskData);
        if (kontoadminGroupIDs.has(taskData.TaskUserGroupID)) return true;
        const ug = user.userGroupAssignments.map((uga: any) => uga.userGroup).find((ug: any) => ug.userGroupID === taskData.TaskUserGroupID);
        return (ug ? isUserGroupAdmin(ug) : false);
      })
      return filteredTasks;
    }

    if(user){
      fetchUserTasks();
    }
  }, [user]);

  const getTaskData = (taskData: TaskData | string): TaskData  => {
    const data: TaskData = JSON.parse(taskData as string) as TaskData;

    return data;
  }

  const isUserGroupAdmin = (userGroup: any): boolean => {
    if(user){
      const isGroupAdmin = userGroup.userGroupAssignments?.some((member: any) =>
        member.user?.userID === user.user.userID && member.roles?.some((r: any) => r.roleName === "Behörighetsansvarig")
      );
      
      return isGroupAdmin 
      || user.roles.some((r: RoleDTO) => r.roleType == 'General');
    }
    else return false;
  }


  const toggleSupportDrawer = () => {
    setShowSupportDrawer(!showSupportDrawer);
  };
  const toggleUserDrawer = () => {
    setShowUserDrawer(!showUserDrawer);
  };
  const toggleSettingsDialog = () => {
    setShowSettingDialog(!showSettingsDialog);
  }

  const openSystemDialog = () => {
    setShowSystemDialog(true);
  }

  const closeSystemDialog = () => {
    setShowSystemDialog(false);
  }

  const logOut = async () => {
    try {
      const response = await authService.logout();
      setUser(null);
      setUserTasks([]);
      window.location.reload();
      
    } catch (err: any) {
      console.log(err);
    }
  }

  const ToolsSubMenu = () => {
    const hasNewsPermission = featurePermissions?.some(fp => fp.action === 'Create news'
      || fp.action === 'Edit news'
      || fp.action === 'Delete news');

    // Hide the whole "Verktyg" entry if none of its sub items would be visible
    if (!hasNewsPermission) return null;

    return (
      <Menu>
        <MenuTrigger disableButtonEnhancement>
          <MenuItem icon={<WrenchIcon/>}>Verktyg</MenuItem>
        </MenuTrigger>

        <MenuPopover>
          <MenuList>
            <MenuItem onClick={() => setOpenNewsDrawer(true)} icon={<ClipboardEditIcon />}>Skapa/Redigera nyheter</MenuItem>
          </MenuList>
        </MenuPopover>
      </Menu>
    );
  };

  return (
    <>
      <div className="h-11 bg-[#373A3C] top-0 z-10">
        <div className="max-w-[1480px] flex items-center h-full justify-between px-3 mx-auto">
          <div className="flex gap-2 items-center">
            <img src="/sk_banner.png" alt="Skånes kommuner logo" />
            <p className="text-lg text-white">Upphandlat</p>
          </div>

          <div className="flex gap-2 font-medium text-white">
            {user && <button className="flex gap-2 items-center" onClick={toggleSupportDrawer}>
              Hjälp
              <QuestionCircleFilled style={{ fontSize: '16px' }} />
            </button>}
            {/* <button className="flex items-center">
              <p className="pl-4 pr-2">Test Testsson</p>
              <MoreVerticalFilled />
            </button> */}
            {user && <Menu>
              <MenuTrigger disableButtonEnhancement>
                <MenuButton
                  appearance="transparent"
                  shape="circular"
                  icon={null}
                >
                  <div className="flex items-center text-white">
                    <p className="pl-4 pr-2">{user.user.firstName + " " + user.user.lastName}</p>
                    <MoreVertical24Regular style={{ color: "white", fontSize: 24 }} />
                  </div>
                </MenuButton>
              </MenuTrigger>
              <MenuPopover>
                <MenuList>
                  <MenuItem onClick={toggleSettingsDialog} icon={<PersonSettingIcon />}>
                    Inställningar
                  </MenuItem>
                  <ToolsSubMenu/>
                  {featurePermissions?.some(p => p.action == 'See user menu') && <MenuItem onClick={toggleUserDrawer} icon={<PeopleTeamIcon/>}>
                    Användare
                  </MenuItem>}
                  {featurePermissions?.some(p => p.action == 'System page') && <MenuItem onClick={openSystemDialog} icon={<SettingsIcon/>}>
                    System
                  </MenuItem>}
                  <MenuItem onClick={logOut} icon={<SignOutIcon/>}>
                    Logga ut
                  </MenuItem>
                </MenuList>
              </MenuPopover>
            </Menu>}
          </div>
        </div>
      </div>
      <SupportDrawer open={showSupportDrawer} onClose={toggleSupportDrawer} />
      <UsersDrawer open={showUserDrawer} onClose={toggleUserDrawer} />
      <UserSettingsDialog open={showSettingsDialog} onClose={toggleSettingsDialog} />
      <NewsDrawer open={openNewsDrawer} onClose={() => setOpenNewsDrawer(false)}/>
      <System isOpen={showSystemDialog} onClose={closeSystemDialog} />
    </>
  );
};

export default Header;
